import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Mail, Phone, MapPin, Briefcase, GraduationCap, Languages, Sparkles, Loader2, ShieldQuestion } from "lucide-react";
import api from "@/lib/api";

export default function PublicProfilePage() {
    const { shareId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/public/profile/${shareId}`)
            .then(({ data }) => setData(data))
            .catch(() => setError(true));
    }, [shareId]);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
                <div className="bg-white rounded-2xl shadow-sm p-10 text-center max-w-md" data-testid="public-profile-notfound">
                    <ShieldQuestion size={36} className="mx-auto text-slate-300" />
                    <h1 className="font-heading text-lg font-bold text-slate-900 mt-3">Profil nicht verfügbar</h1>
                    <p className="text-sm text-slate-500 mt-1">Dieses Profil ist nicht (mehr) öffentlich freigegeben.</p>
                    <Link to="/" className="mt-5 inline-block px-5 py-2.5 rounded-full text-sm font-semibold btn-primary-grad">Zu Bewerbung24.ai</Link>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100">
                <Loader2 size={28} className="animate-spin text-slate-400" />
            </div>
        );
    }

    const skills = data.skills || {};

    return (
        <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="public-profile-page">
            <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <div className="relative h-40 sm:h-48">
                        {data.banner ? (
                            <img src={`data:image/jpeg;base64,${data.banner}`} alt="" className="absolute inset-0 h-full w-full object-cover" />
                        ) : (
                            <div className="absolute inset-0" style={{ background: "var(--gradient-primary)" }}>
                                <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 85% 20%, #fff 0, transparent 28%), radial-gradient(circle at 65% 90%, #fff 0, transparent 22%)" }} />
                            </div>
                        )}
                    </div>
                    <div className="px-6 sm:px-8 pb-6">
                        <div className="-mt-14">
                            <div className="h-28 w-28 rounded-full ring-4 ring-white overflow-hidden bg-white shadow-md inline-flex items-center justify-center">
                                {data.photo ? (
                                    <img src={`data:image/jpeg;base64,${data.photo}`} alt={data.fullname} className="h-full w-full object-cover" />
                                ) : (
                                    <div className="h-full w-full flex items-center justify-center text-white text-3xl font-bold" style={{ background: "var(--gradient-primary)" }}>
                                        {(data.fullname || "?")[0]}
                                    </div>
                                )}
                            </div>
                        </div>
                        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-3" data-testid="public-name">{data.fullname}</h1>
                        <p className="text-sm text-slate-600 mt-0.5">{data.headline}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                            {data.city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {data.city}</span>}
                            {data.email && <a href={`mailto:${data.email}`} className="inline-flex items-center gap-1 hover:text-red-600"><Mail size={11} /> {data.email}</a>}
                            {data.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {data.phone}</span>}
                        </div>
                        {data.email && (
                            <a href={`mailto:${data.email}?subject=Ihre Bewerbung`} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold btn-primary-grad" data-testid="public-contact-btn">
                                <Mail size={14} /> Kontakt aufnehmen
                            </a>
                        )}
                    </div>
                </div>

                {(data.summary || (data.strengths || []).length > 0) && (
                    <div className="bg-white rounded-2xl shadow-sm p-6 mt-4">
                        <h2 className="font-heading text-base font-bold text-slate-900 mb-3">Über mich</h2>
                        {data.summary && <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{data.summary}</p>}
                        {(data.strengths || []).length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {data.strengths.map((s) => (
                                    <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-50 text-red-700"><Sparkles size={10} /> {s}</span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {((skills.hard || []).length > 0 || (skills.languages || []).length > 0) && (
                    <div className="bg-white rounded-2xl shadow-sm p-6 mt-4">
                        <h2 className="font-heading text-base font-bold text-slate-900 mb-3">Skills</h2>
                        <div className="flex flex-wrap gap-1.5">
                            {(skills.hard || []).map((s) => <span key={s} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-slate-200 text-slate-700">{s}</span>)}
                            {(skills.soft || []).map((s) => <span key={s} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-50 border border-slate-100 text-slate-500">{s}</span>)}
                        </div>
                        {(skills.languages || []).length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-1.5">
                                {skills.languages.map((l, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 border border-amber-100 text-amber-800">
                                        <Languages size={11} /> {l.language}{l.level ? ` · ${l.level}` : ""}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {(data.experience || []).length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm p-6 mt-4">
                        <h2 className="font-heading text-base font-bold text-slate-900 mb-4">Berufserfahrung</h2>
                        <div className="space-y-5">
                            {data.experience.map((e, i) => (
                                <div key={i} className="flex gap-3">
                                    <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Briefcase size={17} className="text-slate-500" /></div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-slate-900 text-sm">{e.position}</p>
                                        <p className="text-xs text-slate-600">{e.company}</p>
                                        {e.period && <p className="text-xs text-slate-400 mt-0.5">{e.period}</p>}
                                        {e.description && <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{e.description}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {(data.education || []).length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm p-6 mt-4">
                        <h2 className="font-heading text-base font-bold text-slate-900 mb-4">Ausbildung</h2>
                        <div className="space-y-5">
                            {data.education.map((e, i) => (
                                <div key={i} className="flex gap-3">
                                    <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><GraduationCap size={17} className="text-slate-500" /></div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-slate-900 text-sm">{e.degree || e.institution}</p>
                                        {e.degree && <p className="text-xs text-slate-600">{e.institution}</p>}
                                        <p className="text-xs text-slate-400 mt-0.5">{e.period}{e.grade ? ` · Note ${e.grade}` : ""}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-6 text-center pb-4">
                    <p className="text-xs text-slate-400">Erstellt mit</p>
                    <Link to="/" className="font-heading font-bold text-slate-700 hover:text-red-600">Bewerbung24<span className="gradient-text">.ai</span></Link>
                    <p className="text-xs text-slate-400 mt-1">KI-Bewerbungen in 30 Sekunden — <Link to="/registrieren" className="text-red-600 font-semibold hover:underline">eigenes Profil erstellen</Link></p>
                </div>
            </div>
        </div>
    );
}
